import { Check, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBrandDetails, useBrands, type BrandListItem } from '@/features/brands';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';
import { StatusBadge } from '@/shared/components/status-badge';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { attentionSeverity } from '@/shared/lib/catalogue-labels';
import { cn } from '@/shared/lib/utils';

import { usePaymentChannels } from '../api/pricing.queries';
import type { PricingProviderCandidate } from '../types/pricing.types';
import { EMPTY_SELECTION, type CalculationSelection } from './calculation-selection';
import { SelectionChecks } from './selection-checks';
import { denominationHint, type BrandDenomination } from './selection-hints';

const AUTO = 'auto';
const TRIGGER = 'h-9 w-full text-xs';

/** A numbered input; the number turns into a tick once the step has a value. */
function Field({
  id,
  step,
  label,
  done,
  help,
  children,
}: {
  id: string;
  step: number;
  label: string;
  done: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id} className="flex items-center gap-2 text-xs">
        <span
          aria-hidden
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold tabular-nums',
            done
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border text-muted-foreground',
          )}
        >
          {done ? <Check className="size-3" /> : step}
        </span>
        {label}
      </Label>
      {children}
      {help && <p className="text-muted-foreground text-[11px]">{help}</p>}
    </div>
  );
}

/**
 * What the brand list already says about the brand in this country (display
 * only — the backend decides every state): shown beside each option so a brand
 * that cannot be sold stands out before it is picked.
 */
function brandHint(brand: BrandListItem): SearchableOption['hint'] {
  if (!brand.isActive) return { text: 'Inactive', variant: 'danger' };
  const summary = brand.countrySummary;
  if (summary) {
    if (summary.offered === 0) return { text: 'Not offered', variant: 'neutral' };
    if (summary.sellability === 'None') return { text: 'Cannot be bought', variant: 'danger' };
    if (summary.sellability === 'Some')
      return { text: `${summary.sellable}/${summary.offered} buyable`, variant: 'warning' };
  }
  if (brand.attention.some((code) => attentionSeverity(code) === 'danger'))
    return { text: 'Needs attention', variant: 'warning' };
  return undefined;
}

/**
 * Brand → Store/Region → Denomination → Payment channel → Provider, for the
 * Admin country chosen in the header (the API re-checks it against the staff
 * scope). Options come from the Admin catalogue and channel APIs; the provider
 * list is the candidates the engine itself evaluated for the item.
 */
export function CalculationInputs({
  countryId,
  countryLabel,
  value,
  onChange,
  onReset,
  brandSearch,
  onBrandSearchChange,
  providers,
}: {
  countryId: number;
  countryLabel: string;
  value: CalculationSelection;
  onChange: (next: CalculationSelection) => void;
  onReset: () => void;
  brandSearch: string;
  onBrandSearchChange: (search: string) => void;
  providers: PricingProviderCandidate[];
}) {
  const channels = usePaymentChannels(countryId);
  const search = useDebouncedValue(brandSearch.trim());
  const brands = useBrands({
    page: 1,
    pageSize: 50,
    search: search || undefined,
    countryId,
    sort: 'name',
    direction: 'asc',
  });
  const details = useBrandDetails(value.brandId ?? undefined);

  const stateIn = (denomination: BrandDenomination) =>
    denomination.countries.find((state) => state.countryId === countryId);

  const regions = (details.data?.regions ?? []).filter((region) =>
    details.data?.denominations.some((d) => d.regionId === region.id && d.isActive),
  );
  const denominations = (details.data?.denominations ?? []).filter(
    (d) => d.regionId === value.regionId && d.isActive,
  );
  const activeChannels = (channels.data ?? []).filter((channel) => channel.isActive);
  const brandOptions: SearchableOption[] = (brands.data?.items ?? []).map((brand) => ({
    value: String(brand.id),
    label: brand.name,
    hint: brandHint(brand),
  }));
  const denominationOptions: SearchableOption[] = denominations.map((denomination) => ({
    value: String(denomination.id),
    label: denomination.name,
    hint: denominationHint(stateIn(denomination)),
  }));
  /** A region none of whose active denominations can be bought here. */
  const regionBlocked = (regionId: number) =>
    !(details.data?.denominations ?? []).some(
      (d) => d.regionId === regionId && d.isActive && stateIn(d)?.sellable,
    );

  const hasSelection = Object.values(value).some((v) => v !== null) || brandSearch !== '';

  return (
    <section
      aria-label="What to price"
      className="border-border bg-card flex flex-col rounded-lg border"
    >
      <header className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">What to price</h2>
          <p className="text-muted-foreground text-xs">
            Pricing for <span className="text-foreground font-medium">{countryLabel}</span> — change
            it with the country selector in the header.
          </p>
        </div>
        {hasSelection && (
          <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs" onClick={onReset}>
            <RotateCcw className="size-3.5" aria-hidden />
            Start over
          </Button>
        )}
      </header>

      <div className="flex flex-col gap-4 p-4">
        <fieldset className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <legend className="text-muted-foreground mb-2 text-[11px] font-semibold tracking-wide uppercase">
            Item
          </legend>
          <Field id="calc-brand" step={1} label="Brand" done={value.brandId !== null}>
            <SearchableSelect
              id="calc-brand"
              label="Brand"
              value={value.brandId === null ? null : String(value.brandId)}
              selectedLabel={details.data?.brand.name}
              options={brandOptions}
              onChange={(id) =>
                onChange({
                  ...EMPTY_SELECTION,
                  brandId: Number(id),
                  paymentChannelCode: value.paymentChannelCode,
                })
              }
              placeholder="Search and select a brand"
              searchPlaceholder="Type a brand name…"
              emptyText="No brand matches."
              search={brandSearch}
              onSearchChange={onBrandSearchChange}
              loading={brands.isFetching && brandOptions.length === 0}
              triggerClassName={TRIGGER}
            />
          </Field>

          <Field id="calc-region" step={2} label="Store / Region" done={value.regionId !== null}>
            <Select
              value={value.regionId?.toString() ?? ''}
              onValueChange={(id) =>
                onChange({ ...value, regionId: Number(id), denominationId: null, providerId: null })
              }
              disabled={value.brandId === null || regions.length === 0}
            >
              <SelectTrigger id="calc-region" className={TRIGGER} aria-label="Store / Region">
                <SelectValue
                  placeholder={
                    value.brandId === null ? 'Choose a brand first' : 'Select a store / region'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id.toString()} className="text-xs">
                    {region.name ?? region.code ?? `Region ${region.id}`}
                    {regionBlocked(region.id) && (
                      <StatusBadge variant="danger" className="ms-2">
                        Nothing buyable
                      </StatusBadge>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            id="calc-denomination"
            step={3}
            label="Denomination"
            done={value.denominationId !== null}
          >
            <SearchableSelect
              id="calc-denomination"
              label="Denomination"
              value={value.denominationId === null ? null : String(value.denominationId)}
              options={denominationOptions}
              onChange={(id) =>
                onChange({ ...value, denominationId: Number(id), providerId: null })
              }
              placeholder={
                value.regionId === null
                  ? 'Choose a store / region first'
                  : 'Search and select a denomination'
              }
              searchPlaceholder="Type a denomination…"
              emptyText="No denomination matches."
              disabled={value.regionId === null}
              triggerClassName={TRIGGER}
            />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <legend className="text-muted-foreground mb-2 text-[11px] font-semibold tracking-wide uppercase">
            Checkout
          </legend>
          <Field
            id="calc-channel"
            step={4}
            label="Payment channel"
            done={value.paymentChannelCode !== null}
          >
            <Select
              value={value.paymentChannelCode ?? ''}
              onValueChange={(code) => onChange({ ...value, paymentChannelCode: code })}
            >
              <SelectTrigger id="calc-channel" className={TRIGGER} aria-label="Payment channel">
                <SelectValue placeholder="Select a payment channel" />
              </SelectTrigger>
              <SelectContent>
                {activeChannels.map((channel) => (
                  <SelectItem key={channel.code} value={channel.code} className="text-xs">
                    {channel.name} ({channel.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            id="calc-provider"
            step={5}
            label="Provider"
            done={value.denominationId !== null}
            help={
              value.providerId === null
                ? 'Auto picks the provider exactly as checkout does.'
                : 'A chosen provider is a simulation, not what checkout does.'
            }
          >
            <Select
              value={value.providerId?.toString() ?? AUTO}
              onValueChange={(id) =>
                onChange({ ...value, providerId: id === AUTO ? null : Number(id) })
              }
              disabled={value.denominationId === null}
            >
              <SelectTrigger id="calc-provider" className={TRIGGER} aria-label="Provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO} className="text-xs">
                  Auto — what checkout does
                </SelectItem>
                {providers.map((provider) => (
                  <SelectItem
                    key={provider.providerId}
                    value={provider.providerId.toString()}
                    className="text-xs"
                  >
                    {provider.providerName ?? `Provider ${provider.providerId}`} (simulation)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </fieldset>
      </div>

      {details.data && (
        <SelectionChecks
          details={details.data}
          countryId={countryId}
          countryLabel={countryLabel}
          denominationId={value.denominationId}
        />
      )}
    </section>
  );
}
